/**
 * The dialog the Reference data screen adds and edits through (f2.2).
 *
 * The prototype's `modal-scrim` / `modal` / `modal-head` / `modal-body` /
 * `modal-foot` shape, as a component, so the three panels share one
 * behaviour: Escape closes, the first field takes focus, and a click on the
 * scrim does NOT close -- a stray click must never discard typed values that
 * the server has already refused once.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

export function Modal({
  title,
  subtitle,
  onClose,
  onSubmit,
  busy = false,
  submitDisabled = false,
  submitTitle,
  submitLabel,
  submitIcon,
  submitClassName = 'btn btn-primary',
  narrow = false,
  wide = false,
  layout = 'grid',
  children,
  message,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  onSubmit: () => void;
  busy?: boolean;
  /** Disable only the submit (f10.2: a month that already has a cycle). */
  submitDisabled?: boolean;
  /** A tooltip on the submit, e.g. why it is disabled (f12.3: the first blocker). */
  submitTitle?: string;
  submitLabel: string;
  /** An icon beside the submit label, as the prototype's confirmations have. */
  submitIcon?: ReactNode;
  /** `btn btn-danger` for a destructive confirmation (f4.1's deactivate). */
  submitClassName?: string;
  /** The prototype's `modal narrow`, used by its confirmation dialogs. */
  narrow?: boolean;
  /**
   * The prototype's `modal wide`, used by a dialog carrying several fieldsets
   * (f5.1's Business Unit create/edit). Ignored when `narrow` is set, because
   * a dialog cannot be both and silently picking one would hide the mistake.
   */
  wide?: boolean;
  /**
   * `grid` wraps the body in the two-column field grid the reference-data
   * dialogs use; `plain` leaves the body alone, for a confirmation whose
   * content is an alert and a single control rather than a form layout.
   */
  layout?: 'grid' | 'plain';
  children: ReactNode;
  /** The server's general refusal, when it named no single field. */
  message?: string | null;
}) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    // The first control, so the dialog is usable without reaching for a mouse.
    panel.current?.querySelector<HTMLElement>('input, select, textarea')?.focus();
  }, []);

  return (
    <div className="modal-scrim" role="presentation">
      <div
        className={narrow ? 'modal narrow' : wide ? 'modal wide' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panel}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          <div className="modal-head">
            <div>
              <h3>{title}</h3>
              {subtitle && <div className="m-sub">{subtitle}</div>}
            </div>
            <button
              type="button"
              className="icon-btn"
              onClick={onClose}
              aria-label="Close"
              disabled={busy}
            >
              <X aria-hidden="true" />
            </button>
          </div>
          <div className="modal-body">
            {message && (
              <div className="alert alert-danger ref-dialog-alert" role="alert">
                <div>
                  <div className="a-body">{message}</div>
                </div>
              </div>
            )}
            {layout === 'grid' ? <div className="grid grid-2">{children}</div> : children}
          </div>
          <div className="modal-foot">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="submit"
              className={submitClassName}
              disabled={busy || submitDisabled}
              title={submitTitle}
            >
              {submitIcon}
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** One labelled control in a dialog, with its server-bound problem. */
export function DialogField({
  label,
  htmlFor,
  problem,
  hint,
  span = false,
  required = false,
  children,
}: {
  label: string;
  htmlFor: string;
  problem?: string | null;
  hint?: string;
  span?: boolean;
  /** The prototype's `label.req` marker (f15.2's Placement fields). */
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`field${span ? ' span-2' : ''}${problem ? ' has-problem' : ''}`}>
      <label htmlFor={htmlFor} className={required ? 'req' : undefined}>
        {label}
      </label>
      {children}
      {hint && !problem && <span className="hint">{hint}</span>}
      {problem && (
        <span className="field-error" role="alert">
          {problem}
        </span>
      )}
    </div>
  );
}
